// 아바타 망토 2차(L1030): CPU 천 시뮬. 격자 입자 5×7, 맨 윗줄(칼라)은 망토 그룹(Spine2 자식)에 고정.
// Verlet + 거리 제약 + 휴지 모양 복원력 + 이동 바람, 몸 충돌(캡슐 + 등 평면)로 관통을 막는다.
// 시뮬은 월드 공간. 렌더는 capeGeo의 13×10 정점을 "입자의 휴지 대비 이동량(로컬)"을 쌍선형 보간해 휴지 모양에 더한다
// → 아랫단 물결 같은 세부 모양은 그대로 남고, 입자가 몸 밖이면 정점도 몸 밖이다.
// 프레임 루프에서는 객체를 만들지 않는다(모든 배열은 생성자에서 한 번 할당, 계산은 스칼라).
import * as THREE from 'three/webgpu';
import { CAPE_COLS, CAPE_ROWS, capeNormals } from './capeGeo';

export const CLOTH_COLS = 5;
export const CLOTH_ROWS = 7;
/** 고정 스텝 60Hz, 프레임당 최대 4스텝 */
export const CLOTH_STEP = 1 / 60;
export const CLOTH_MAX_STEPS = 4;
/** 순간이동 판정: 한 프레임에 칼라가 이만큼(m) 넘게 움직이면 휴지 자세로 리셋 */
export const CLOTH_TELEPORT_M = 1.5;
/** dt가 이보다 크면(탭 전환 등) 리셋 */
export const CLOTH_MAX_DT = 0.25;
/** 입자를 캡슐 반지름 밖으로 밀어낼 때 더하는 여유(m, 월드) */
export const CLOTH_MARGIN = 0.015;
/** 등 평면: 몸통 축에서 이만큼(m, 월드) 뒤보다 앞으로 못 나온다 */
export const CLOTH_PLANE_OFFSET = 0.02;
/** 몸 캡슐 반지름(모델 배율 전 m). 월드 반지름 = 이 값 × worldScale(아바타는 1.5) */
export const BODY_RADIUS = { torso: 0.14, thigh: 0.09, shin: 0.06 } as const;

const GRAVITY = 4;
const DAMP = 0.985;
const WIND_K = 0.9;
const LIFT_K = 0.12;
/** 칼라 줄 위로 이만큼(m, × 그룹 배율)보다 더 올라가지 못한다 — 망토가 머리 위로 뒤집히는 것을 막는 단방향 가드 */
export const CLOTH_FLIP_MARGIN = 0.03;
/** 휴지 위치보다 그룹 위쪽으로 이만큼(m, × 그룹 배율) 넘게 뜨지 못한다 */
export const CLOTH_LIFT_MAX = 0.06;
const ITER = 4;
const SHEAR_STIFF = 0.3;

export interface ClothBodyBones {
  hips?: THREE.Object3D; spine2?: THREE.Object3D;
  upLegL?: THREE.Object3D; legL?: THREE.Object3D; footL?: THREE.Object3D;
  upLegR?: THREE.Object3D; legR?: THREE.Object3D; footR?: THREE.Object3D;
}

/** 입자 격자 → 원본 정점 행/열 번호 */
const PCOL: readonly number[] = Array.from({ length: CLOTH_COLS }, (_, i) => Math.round(i * CAPE_COLS / (CLOTH_COLS - 1)));
const PROW: readonly number[] = Array.from({ length: CLOTH_ROWS }, (_, j) => Math.round(j * CAPE_ROWS / (CLOTH_ROWS - 1)));

export class CapeCloth {
  /** 입자 월드 위치 [x,y,z]*n, 입자 번호 = row*CLOTH_COLS + col */
  readonly pos: Float32Array;
  private readonly prev: Float32Array;
  private readonly restLocal: Float32Array;
  private readonly restWorld: Float32Array;
  private readonly rowT: Float32Array;
  readonly count = CLOTH_COLS * CLOTH_ROWS;
  // 거리 제약
  private readonly eA: Uint8Array; private readonly eB: Uint8Array;
  private readonly eLen: Float32Array; private readonly eK: Float32Array;
  private readonly edgeCount: number;
  // 렌더 보간 표(정점마다 입자 4개 + 가중치)
  private readonly restVert: Float32Array;
  private readonly vIdx: Uint8Array; private readonly vW: Float32Array;
  private readonly delta: Float32Array;
  // 몸 충돌
  readonly capA: Float32Array; readonly capB: Float32Array; readonly capR: Float32Array;
  private capPairs: { a: THREE.Object3D; b: THREE.Object3D; r: number }[] = [];
  readonly capCount: number;
  /** 몸통 축 [hips xyz, spine2 xyz] + 앞방향 [hips fwd, spine2 fwd] */
  private readonly body = new Float32Array(12);
  private readonly hips: THREE.Object3D | undefined; private readonly spine2: THREE.Object3D | undefined;
  private readonly inv = new THREE.Matrix4();
  private readonly worldScale: number;
  private acc = 0;
  private inited = false;
  private cx = 0; private cy = 0; private cz = 0;
  private vx = 0; private vy = 0; private vz = 0;
  private sc = 1;
  /** 직전 프레임 앵커 행렬·칼라 줄 위치(프레임 사이 이동을 서브스텝에 나누기 위해) */
  private readonly lastM = new Float32Array(16);
  private readonly pinPrev = new Float32Array(CLOTH_COLS * 3);
  private readonly mA = new THREE.Matrix4(); private readonly mB = new THREE.Matrix4();
  /** 그룹 위(+Y)·앞(+Z) 단위 방향과 원점(월드) */
  private readonly gframe = new Float32Array(9);

  /**
   * @param rest capeShape().position (그룹 로컬)
   * @param anchor 망토 그룹(칼라 고정 기준). matrixWorld는 호출 쪽이 최신으로 유지한다
   * @param worldScale 몸 캡슐 반지름 배율(아바타 = 모델 배율 1.5)
   */
  constructor(rest: Float32Array, private readonly anchor: THREE.Object3D, bones: ClothBodyBones, worldScale = 1) {
    const n = this.count;
    this.worldScale = worldScale;
    this.pos = new Float32Array(n * 3); this.prev = new Float32Array(n * 3);
    this.restLocal = new Float32Array(n * 3); this.restWorld = new Float32Array(n * 3);
    this.rowT = new Float32Array(n);
    const stride = CAPE_COLS + 1;
    for (let j = 0; j < CLOTH_ROWS; j++) for (let i = 0; i < CLOTH_COLS; i++) {
      const k = j * CLOTH_COLS + i, v = (PROW[j] * stride + PCOL[i]) * 3;
      this.restLocal[k * 3] = rest[v]; this.restLocal[k * 3 + 1] = rest[v + 1]; this.restLocal[k * 3 + 2] = rest[v + 2];
      this.rowT[k] = j / (CLOTH_ROWS - 1);
    }
    // 제약: 세로·가로(강), 대각(약)
    const ea: number[] = [], eb: number[] = [], ek: number[] = [];
    const link = (a: number, b: number, k: number) => { ea.push(a); eb.push(b); ek.push(k); };
    for (let j = 0; j < CLOTH_ROWS; j++) for (let i = 0; i < CLOTH_COLS; i++) {
      const k = j * CLOTH_COLS + i;
      if (i + 1 < CLOTH_COLS) link(k, k + 1, 1);
      if (j + 1 < CLOTH_ROWS) link(k, k + CLOTH_COLS, 1);
      if (i + 1 < CLOTH_COLS && j + 1 < CLOTH_ROWS) { link(k, k + CLOTH_COLS + 1, SHEAR_STIFF); link(k + 1, k + CLOTH_COLS, SHEAR_STIFF); }
    }
    this.edgeCount = ea.length;
    this.eA = Uint8Array.from(ea); this.eB = Uint8Array.from(eb); this.eK = Float32Array.from(ek);
    this.eLen = new Float32Array(this.edgeCount);
    for (let e = 0; e < this.edgeCount; e++) {
      const a = ea[e] * 3, b = eb[e] * 3;
      this.eLen[e] = Math.hypot(this.restLocal[a] - this.restLocal[b], this.restLocal[a + 1] - this.restLocal[b + 1], this.restLocal[a + 2] - this.restLocal[b + 2]);
    }
    // 렌더 보간 표
    const vc = (CAPE_COLS + 1) * (CAPE_ROWS + 1);
    this.restVert = Float32Array.from(rest);
    this.vIdx = new Uint8Array(vc * 4); this.vW = new Float32Array(vc * 4); this.delta = new Float32Array(n * 3);
    const seg = (P: readonly number[], x: number): [number, number] => {
      let s = 0;
      while (s < P.length - 2 && x > P[s + 1]) s++;
      return [s, (x - P[s]) / (P[s + 1] - P[s])];
    };
    for (let R = 0; R <= CAPE_ROWS; R++) for (let C = 0; C <= CAPE_COLS; C++) {
      const [j, fr] = seg(PROW, R), [i, fc] = seg(PCOL, C), q = (R * stride + C) * 4;
      const b = j * CLOTH_COLS + i;
      this.vIdx[q] = b; this.vIdx[q + 1] = b + 1; this.vIdx[q + 2] = b + CLOTH_COLS; this.vIdx[q + 3] = b + CLOTH_COLS + 1;
      this.vW[q] = (1 - fr) * (1 - fc); this.vW[q + 1] = (1 - fr) * fc; this.vW[q + 2] = fr * (1 - fc); this.vW[q + 3] = fr * fc;
    }
    // 몸 캡슐: 본을 못 찾은 쪽은 생략
    this.hips = bones.hips; this.spine2 = bones.spine2;
    const P = this.capPairs;
    const add = (a: THREE.Object3D | undefined, b: THREE.Object3D | undefined, r: number) => { if (a && b) P.push({ a, b, r: r * worldScale }); };
    add(bones.hips, bones.spine2, BODY_RADIUS.torso);
    add(bones.upLegL, bones.legL, BODY_RADIUS.thigh); add(bones.upLegR, bones.legR, BODY_RADIUS.thigh);
    add(bones.legL, bones.footL, BODY_RADIUS.shin); add(bones.legR, bones.footR, BODY_RADIUS.shin);
    this.capCount = P.length;
    this.capA = new Float32Array(P.length * 3); this.capB = new Float32Array(P.length * 3); this.capR = new Float32Array(P.length);
    for (let k = 0; k < P.length; k++) this.capR[k] = P[k].r;
  }

  /** 휴지 자세로 되돌린다(장착·순간이동·씬 전환). 다음 update에서 다시 맞춘다 */
  reset(): void { this.inited = false; this.acc = 0; this.vx = this.vy = this.vz = 0; }

  private refreshFrame(): void {
    const m = this.anchor.matrixWorld.elements;
    this.sc = Math.hypot(m[0], m[1], m[2]);
    const g = this.gframe, ly = Math.hypot(m[4], m[5], m[6]) || 1, lz = Math.hypot(m[8], m[9], m[10]) || 1;
    g[0] = m[4] / ly; g[1] = m[5] / ly; g[2] = m[6] / ly; g[3] = m[8] / lz; g[4] = m[9] / lz; g[5] = m[10] / lz;
    g[6] = m[12]; g[7] = m[13]; g[8] = m[14];
    const r = this.restLocal, w = this.restWorld;
    for (let k = 0; k < this.count; k++) {
      const x = r[k * 3], y = r[k * 3 + 1], z = r[k * 3 + 2];
      w[k * 3] = m[0] * x + m[4] * y + m[8] * z + m[12];
      w[k * 3 + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
      w[k * 3 + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
    }
    // 칼라 줄 고정
    for (let i = 0; i < CLOTH_COLS * 3; i++) { this.pos[i] = w[i]; this.prev[i] = w[i]; }
    // 몸 캡슐·몸통 축
    const cp = this.capPairs;
    for (let k = 0; k < cp.length; k++) {
      const a = cp[k].a.matrixWorld.elements, b = cp[k].b.matrixWorld.elements;
      this.capA[k * 3] = a[12]; this.capA[k * 3 + 1] = a[13]; this.capA[k * 3 + 2] = a[14];
      this.capB[k * 3] = b[12]; this.capB[k * 3 + 1] = b[13]; this.capB[k * 3 + 2] = b[14];
    }
    if (this.hips && this.spine2) {
      const h = this.hips.matrixWorld.elements, s = this.spine2.matrixWorld.elements, bd = this.body;
      bd[0] = h[12]; bd[1] = h[13]; bd[2] = h[14]; bd[3] = s[12]; bd[4] = s[13]; bd[5] = s[14];
      let l = Math.hypot(h[8], h[9], h[10]) || 1; bd[6] = h[8] / l; bd[7] = h[9] / l; bd[8] = h[10] / l;
      l = Math.hypot(s[8], s[9], s[10]) || 1; bd[9] = s[8] / l; bd[10] = s[9] / l; bd[11] = s[10] / l;
    }
  }

  /** 프레임마다. dt = 실시간 초. 본·그룹의 matrixWorld가 최신이어야 한다 */
  update(dt: number): void {
    if (!(dt > 0)) return;
    const m = this.anchor.matrixWorld.elements;
    const x = m[12], y = m[13], z = m[14];
    const jump = Math.hypot(x - this.cx, y - this.cy, z - this.cz);
    if (!this.inited || dt > CLOTH_MAX_DT || jump > CLOTH_TELEPORT_M) {
      this.refreshFrame();
      this.pos.set(this.restWorld); this.prev.set(this.restWorld);
      this.lastM.set(m);
      this.cx = x; this.cy = y; this.cz = z; this.vx = this.vy = this.vz = 0; this.acc = 0; this.inited = true;
      return;
    }
    // 몸 속도(부드럽게): 바람 계산용
    const k = 1 - Math.exp(-dt * 10);
    this.vx += ((x - this.cx) / dt - this.vx) * k; this.vy += ((y - this.cy) / dt - this.vy) * k; this.vz += ((z - this.cz) / dt - this.vz) * k;
    this.cx = x; this.cy = y; this.cz = z;
    // 직전 프레임의 칼라 줄 위치를 챙긴 뒤 이번 프레임으로 갱신
    for (let i = 0; i < CLOTH_COLS * 3; i++) this.pinPrev[i] = this.pos[i]; // subarray 뷰도 새 객체라 프레임 루프에서 쓰지 않는다
    const lm = this.lastM;
    this.refreshFrame();
    this.acc += dt;
    let n = Math.floor(this.acc / CLOTH_STEP + 1e-6), fx = 0;
    if (n > CLOTH_MAX_STEPS) { fx = Math.min(1, (this.acc - CLOTH_MAX_STEPS * CLOTH_STEP) / dt); n = CLOTH_MAX_STEPS; this.acc = 0; } else this.acc = Math.max(0, this.acc - n * CLOTH_STEP);
    if (fx > 0) {
      // 서브스텝 상한을 넘는 시간의 앵커 이동은 입자 전체를 같이 옮겨 흡수한다(관성 없이 상대 움직임만 시뮬)
      this.mA.fromArray(lm).invert(); this.mB.fromArray(m).multiply(this.mA);
      const e = this.mB.elements, p = this.pos, q = this.prev;
      for (let kk = CLOTH_COLS; kk < this.count; kk++) {
        const i = kk * 3;
        for (let arr = 0; arr < 2; arr++) {
          const v = arr === 0 ? p : q, x = v[i], y = v[i + 1], z = v[i + 2];
          v[i] = x + (e[0] * x + e[4] * y + e[8] * z + e[12] - x) * fx;
          v[i + 1] = y + (e[1] * x + e[5] * y + e[9] * z + e[13] - y) * fx;
          v[i + 2] = z + (e[2] * x + e[6] * y + e[10] * z + e[14] - z) * fx;
        }
      }
      for (let i = 0; i < CLOTH_COLS * 3; i++) this.pinPrev[i] += (this.restWorld[i] - this.pinPrev[i]) * fx;
    }
    lm.set(m);
    for (let s = 0; s < n; s++) this.step((s + 1) / n);
  }

  /** alpha = 이번 프레임 안에서 이 서브스텝의 위치(0..1): 칼라 줄을 직전→현재 프레임 사이로 보간한다 */
  private step(alpha: number): void {
    const p = this.pos, q = this.prev, rw = this.restWorld, h2 = CLOTH_STEP * CLOTH_STEP;
    for (let i = 0; i < CLOTH_COLS * 3; i++) { const v = this.pinPrev[i] + (rw[i] - this.pinPrev[i]) * alpha; p[i] = v; q[i] = v; }
    const wx = -this.vx * WIND_K, wy = -this.vy * WIND_K * 0.6, wz = -this.vz * WIND_K;
    const lift = Math.hypot(this.vx, this.vz) * LIFT_K;
    for (let kk = CLOTH_COLS; kk < this.count; kk++) {
      const i = kk * 3, t = this.rowT[kk], spring = 24 + (1 - t) * 40;
      // 바람은 아랫단일수록 세게(t²), 위로 뜨는 힘도 같다. 복원력은 칼라 쪽이 더 단단하다
      const aw = t * t;
      const ax = wx * aw + (rw[i] - p[i]) * spring;
      const ay = (wy + lift) * aw - GRAVITY + (rw[i + 1] - p[i + 1]) * spring;
      const az = wz * aw + (rw[i + 2] - p[i + 2]) * spring;
      let c = p[i]; p[i] = c + (c - q[i]) * DAMP + ax * h2; q[i] = c;
      c = p[i + 1]; p[i + 1] = c + (c - q[i + 1]) * DAMP + ay * h2; q[i + 1] = c;
      c = p[i + 2]; p[i + 2] = c + (c - q[i + 2]) * DAMP + az * h2; q[i + 2] = c;
    }
    const sc = this.sc;
    for (let it = 0; it < ITER; it++) {
      for (let e = 0; e < this.edgeCount; e++) {
        const a = this.eA[e], b = this.eB[e], ia = a * 3, ib = b * 3;
        const dx = p[ib] - p[ia], dy = p[ib + 1] - p[ia + 1], dz = p[ib + 2] - p[ia + 2];
        const d = Math.hypot(dx, dy, dz);
        if (d < 1e-9) continue;
        const f = (d - this.eLen[e] * sc) / d * this.eK[e];
        const wa = a < CLOTH_COLS ? 0 : 1, wb = b < CLOTH_COLS ? 0 : 1, ws = wa + wb;
        if (ws === 0) continue;
        const ca = f * wa / ws, cb = f * wb / ws;
        p[ia] += dx * ca; p[ia + 1] += dy * ca; p[ia + 2] += dz * ca;
        p[ib] -= dx * cb; p[ib + 1] -= dy * cb; p[ib + 2] -= dz * cb;
      }
    }
    // 충돌은 마지막(관통 없음 보장): 캡슐 → 평면 → 캡슐 → 평면
    this.collideFlip(); this.collideCapsules(); this.collidePlane(); this.collideCapsules(); this.collidePlane();
  }

  /** 뒤집힘 가드: 칼라 줄 높이 + 여유보다 위로, 그리고 칼라보다 앞(머리 쪽)으로 못 나간다. 그룹 축 기준 단방향 투영 */
  private collideFlip(): void {
    const p = this.pos, g = this.gframe, lim = CLOTH_FLIP_MARGIN * this.sc, liftMax = CLOTH_LIFT_MAX * this.sc;
    for (let kk = CLOTH_COLS; kk < this.count; kk++) {
      const i = kk * 3, rx = p[i] - g[6], ry = p[i + 1] - g[7], rz = p[i + 2] - g[8];
      const up = rx * g[0] + ry * g[1] + rz * g[2];
      if (up > lim) { const d = up - lim; p[i] -= g[0] * d; p[i + 1] -= g[1] * d; p[i + 2] -= g[2] * d; }
      // 휴지 위치보다 위로 뜨는 양도 제한(급가속·급선회의 채찍 효과로 깃발처럼 치솟는 것을 막는다)
      const lift = (p[i] - this.restWorld[i]) * g[0] + (p[i + 1] - this.restWorld[i + 1]) * g[1] + (p[i + 2] - this.restWorld[i + 2]) * g[2];
      if (lift > liftMax) { const d = lift - liftMax; p[i] -= g[0] * d; p[i + 1] -= g[1] * d; p[i + 2] -= g[2] * d; }
      const fw = rx * g[3] + ry * g[4] + rz * g[5];
      if (fw > lim) { const d = fw - lim; p[i] -= g[3] * d; p[i + 1] -= g[4] * d; p[i + 2] -= g[5] * d; }
    }
  }

  private collideCapsules(): void {
    const p = this.pos, A = this.capA, B = this.capB, R = this.capR;
    const bx = this.hips ? -this.body[6] : 0, by = this.hips ? -this.body[7] : 0, bz = this.hips ? -this.body[8] : -1; // 정확히 겹쳐 방향을 못 정할 때 등 쪽으로
    for (let kk = CLOTH_COLS; kk < this.count; kk++) {
      const i = kk * 3;
      for (let c = 0; c < this.capCount; c++) {
        const o = c * 3;
        const abx = B[o] - A[o], aby = B[o + 1] - A[o + 1], abz = B[o + 2] - A[o + 2];
        const l2 = abx * abx + aby * aby + abz * abz;
        const apx = p[i] - A[o], apy = p[i + 1] - A[o + 1], apz = p[i + 2] - A[o + 2];
        let t = l2 > 1e-12 ? (apx * abx + apy * aby + apz * abz) / l2 : 0;
        t = t < 0 ? 0 : t > 1 ? 1 : t;
        const cx = A[o] + abx * t, cy = A[o + 1] + aby * t, cz = A[o + 2] + abz * t;
        const dx = p[i] - cx, dy = p[i + 1] - cy, dz = p[i + 2] - cz;
        const rr = R[c] + CLOTH_MARGIN * this.worldScale, d2 = dx * dx + dy * dy + dz * dz;
        if (d2 >= rr * rr) continue;
        const d = Math.sqrt(d2);
        if (d < 1e-6) { p[i] = cx + bx * rr; p[i + 1] = cy + by * rr; p[i + 2] = cz + bz * rr; continue; }
        const s = rr / d;
        p[i] = cx + dx * s; p[i + 1] = cy + dy * s; p[i + 2] = cz + dz * s;
      }
    }
  }

  /** 등 평면: 몸통 축(Hips→Spine2) 위 높이에 따라 앞방향을 섞고, 축에서 뒤로 PLANE_OFFSET 이상 떨어져 있게 한다 */
  private collidePlane(): void {
    if (!this.hips || !this.spine2) return;
    const p = this.pos, bd = this.body, off = CLOTH_PLANE_OFFSET * this.worldScale;
    const ax = bd[3] - bd[0], ay = bd[4] - bd[1], az = bd[5] - bd[2], l2 = ax * ax + ay * ay + az * az || 1;
    for (let kk = CLOTH_COLS; kk < this.count; kk++) {
      const i = kk * 3;
      let t = ((p[i] - bd[0]) * ax + (p[i + 1] - bd[1]) * ay + (p[i + 2] - bd[2]) * az) / l2;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      let nx = bd[6] + (bd[9] - bd[6]) * t, ny = bd[7] + (bd[10] - bd[7]) * t, nz = bd[8] + (bd[11] - bd[8]) * t;
      const nl = Math.hypot(nx, ny, nz) || 1; nx /= nl; ny /= nl; nz /= nl;
      const d = (p[i] - bd[0] - ax * t) * nx + (p[i + 1] - bd[1] - ay * t) * ny + (p[i + 2] - bd[2] - az * t) * nz;
      if (d > -off) {
        const push = d + off;
        p[i] -= nx * push; p[i + 1] -= ny * push; p[i + 2] -= nz * push;
      }
    }
  }

  /** 렌더 정점·노멀 채우기(13×10). 입자 월드 → 그룹 로컬 이동량을 보간해 휴지 모양에 더한다 */
  fillVertices(position: Float32Array, normal: Float32Array): void {
    this.inv.copy(this.anchor.matrixWorld).invert();
    const e = this.inv.elements, p = this.pos, rl = this.restLocal, d = this.delta;
    for (let k = 0; k < this.count; k++) {
      const x = p[k * 3], y = p[k * 3 + 1], z = p[k * 3 + 2];
      d[k * 3] = e[0] * x + e[4] * y + e[8] * z + e[12] - rl[k * 3];
      d[k * 3 + 1] = e[1] * x + e[5] * y + e[9] * z + e[13] - rl[k * 3 + 1];
      d[k * 3 + 2] = e[2] * x + e[6] * y + e[10] * z + e[14] - rl[k * 3 + 2];
    }
    const vc = (CAPE_COLS + 1) * (CAPE_ROWS + 1), idx = this.vIdx, w = this.vW, rv = this.restVert;
    for (let v = 0; v < vc; v++) {
      const q = v * 4;
      for (let a = 0; a < 3; a++) {
        position[v * 3 + a] = rv[v * 3 + a] + d[idx[q] * 3 + a] * w[q] + d[idx[q + 1] * 3 + a] * w[q + 1] + d[idx[q + 2] * 3 + a] * w[q + 2] + d[idx[q + 3] * 3 + a] * w[q + 3];
      }
    }
    capeNormals(position, normal);
  }

  /** 망토 지오메트리(position·normal 속성)에 써 넣고 갱신 표시 */
  applyTo(geometry: THREE.BufferGeometry): void {
    const pos = geometry.getAttribute('position') as THREE.BufferAttribute, nor = geometry.getAttribute('normal') as THREE.BufferAttribute;
    this.fillVertices(pos.array as Float32Array, nor.array as Float32Array);
    pos.needsUpdate = true; nor.needsUpdate = true;
  }
}
